import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { Stack } from "@similarweb/ui-components/foundations";

import { filterAccountRulesByTeamMembership } from "app/pages/sales-intelligence/domain/leads/utils/filterAccountRulesByTeamMembership";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { ScoringAddRuleButton } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringAddRuleButton";
import {
    ScoringAddRuleCombobox,
    type ScoringAddRuleTranslationKeys,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringAddRuleCombobox";
import { ScoringBoard } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringBoard";
import { ScoringBoardError } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringBoardError";
import { ScoringBoardLoader } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/scoring-board/ScoringBoardLoader";
import {
    ScoringBoardHeader,
    type ScoringBoardSegmentTranslationKeys,
    ScoringRulesExportButton,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringBoardHeader";
import { ScoringRuleDeleteModal } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleDeleteModal";
import { ScoringRuleWizardModal } from "app/pages/sales-intelligence/modules/lead-scoring-agent/components/ScoringRuleWizardModal";
import useSaveRules from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useSaveRules";
import { useScoringBoardCrud } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardCrud";
import { useScoringBoardModalState } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardModalState";
import { useScoringBoardTrackingService } from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useScoringBoardTrackingService";
import useLeadScoringConfigQuery from "app/pages/sales-intelligence/modules/lead-scoring-agent/query/queries/useLeadScoringConfigQuery";
import { getScoringConfigSignalSource } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/getScoringConfigSignalSource";
import {
    moveRuleBetweenColumns,
    prepareWizardRuleUpdate,
    reconcileSelectedRuleAfterDelete,
    removeRuleFromColumn,
    resolveEffectiveSelectedRuleId,
    resolveScopedRuleLocation,
    SCORING_PRIORITY_COLUMNS,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringBoardUtils";
import {
    buildScoringRulesView,
    countRulesInScoringView,
    countScoringRulesByScope,
    getScoringRulesForSegment,
    type ScoringScopeSegment,
} from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringRulesView";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";
import { useUserTeamMembership } from "app/pages/sales-intelligence/modules/teams/hooks/useUserTeamMembership";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { useAllRawUISignalsGroupedById } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useUISignals";
import { setSignalsSourceAction } from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import {
    type IScoringRuleDto,
    type ScoringConfigRule,
    ScoringConfigType,
} from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";
import { translateSignalsToMapById } from "app/pages/sales-intelligence/sub-modules/signals/utils/translateSignalsToMapById";

const BASE_SEGMENTS = ["all", "shared", "private"] as const satisfies readonly ScoringScopeSegment[];
const ADMIN_SEGMENTS = [...BASE_SEGMENTS, "applicableForMe"] as const satisfies readonly ScoringScopeSegment[];

const EMPTY_SCORING_RULES: ScoringConfigRule = {};

const LOCKED_SCOPE_SEGMENT: Record<ScoringConfigType, ScoringScopeSegment> = {
    [ScoringConfigType.User]: "private",
    [ScoringConfigType.Account]: "shared",
};

type WizardState =
    | { mode: "create"; priority: LeadPriorityScore; scope: ScoringConfigType }
    | { mode: "edit"; priority: LeadPriorityScore; ruleIndex: number; rule: IScoringRuleDto; scope: ScoringConfigType };

type DeleteRuleState = {
    priority: LeadPriorityScore;
    ruleIndex: number;
    scope: ScoringConfigType;
};

export type DualScoringBoardSegmentTranslationKeys = ScoringBoardSegmentTranslationKeys;

/**
 * Either the user picks the scope through the segmented control, or the owner locks the board to a
 * single scope — a locked board has no segments and no scope to choose from when adding a rule.
 */
type DualScoringBoardScopeProps =
    | {
          lockedScope: ScoringConfigType;
          segmentTranslationKeys?: never;
          addRuleTranslationKeys?: never;
      }
    | {
          lockedScope?: undefined;
          segmentTranslationKeys: DualScoringBoardSegmentTranslationKeys;
          addRuleTranslationKeys: ScoringAddRuleTranslationKeys;
      };

export type DualScoringBoardContainerProps = DualScoringBoardScopeProps & {
    filtersList: readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];
    isBootstrapping?: boolean;
    automationPrefix?: string;
    /** Adds the "export to Excel" action to the header — the board's own page, not the rule-picker embeds. */
    isExportEnabled?: boolean;
    applicableForUserOnly?: boolean;
    selectedRuleId?: string | null;
    /** Multi-select alternative to `selectedRuleId`; `onSelect` then acts as a toggle. */
    selectedRuleIds?: ReadonlySet<string>;
    pinSelectedFirst?: boolean;
    /** Marks selected rules with the "used" badge, for boards where selecting a rule assigns it. */
    showUsedBadgeForSelected?: boolean;
    onSelect?: (ruleId: string, priority: LeadPriorityScore) => void;
    /** Returns the text explaining why a rule cannot be selected, or undefined when it can be. */
    getSelectionDisabledReason?: (rule: IScoringRuleDto) => string | undefined;
    /** Multi-select only: lets the owner drop a deleted rule from its selection. */
    onRuleDeleted?: (ruleId: string) => void;
    /** Teams preselected on the wizard's team step when creating a rule from this board. */
    defaultTeamIds?: readonly number[];
    getRuleUsage?: (rule: IScoringRuleDto) => readonly string[];
};

export const DualScoringBoardContainer = ({
    filtersList,
    isBootstrapping = false,
    lockedScope,
    segmentTranslationKeys,
    addRuleTranslationKeys,
    automationPrefix = "scoring-board",
    isExportEnabled = false,
    applicableForUserOnly = false,
    selectedRuleId = null,
    selectedRuleIds,
    pinSelectedFirst,
    showUsedBadgeForSelected,
    onSelect,
    getSelectionDisabledReason,
    onRuleDeleted,
    defaultTeamIds,
    getRuleUsage,
}: DualScoringBoardContainerProps) => {
    const dispatch = useDispatch();
    const canManageShared = salesSettingsHelper.isHighLevelAccount();
    const usesFullAccountScope = canManageShared && !applicableForUserOnly;
    const scoringBoardTracking = useScoringBoardTrackingService();
    const isSelectionMode = onSelect !== undefined;
    const isMultiSelect = selectedRuleIds !== undefined;
    const lockedSegment = lockedScope === undefined ? undefined : LOCKED_SCOPE_SEGMENT[lockedScope];

    const { userTeamIds, isMembershipSuccess, isMembershipLoading } = useUserTeamMembership();

    const showApplicableForMeSegment =
        !lockedSegment && usesFullAccountScope && !!segmentTranslationKeys?.applicableForMe;
    const segments: readonly ScoringScopeSegment[] = showApplicableForMeSegment ? ADMIN_SEGMENTS : BASE_SEGMENTS;

    const {
        data: userConfig,
        isFetching: isUserFetching,
        isError: isUserError,
        refetch: refetchUser,
    } = useLeadScoringConfigQuery(ScoringConfigType.User);
    const {
        data: accountConfig,
        isFetching: isAccountFetching,
        isError: isAccountError,
        refetch: refetchAccount,
    } = useLeadScoringConfigQuery(ScoringConfigType.Account);

    const { saveRules: saveUserRules, isPending: isSavingUser } = useSaveRules(ScoringConfigType.User);
    const { saveRules: saveAccountRules, isPending: isSavingAccount } = useSaveRules(ScoringConfigType.Account);

    const { persist: persistRules, persistOptimistic } = useScoringBoardCrud();

    const rawUISignals = useAllRawUISignalsGroupedById();
    const signalById = useMemo(() => translateSignalsToMapById(Object.values(rawUISignals)), [rawUISignals]);

    const userRules = userConfig?.rules ?? EMPTY_SCORING_RULES;
    const accountRules = accountConfig?.rules ?? EMPTY_SCORING_RULES;

    const applicableAccountRules = useMemo(() => {
        if (!isMembershipSuccess) {
            return accountRules;
        }

        return filterAccountRulesByTeamMembership(accountRules, userTeamIds);
    }, [accountRules, isMembershipSuccess, userTeamIds]);

    const visibleAccountRules = usesFullAccountScope ? accountRules : applicableAccountRules;
    const accountRulesForSegments = visibleAccountRules;

    const combinedView = useMemo(
        () => buildScoringRulesView(userRules, visibleAccountRules),
        [userRules, visibleAccountRules],
    );

    const allAccountRulesView = useMemo(
        () => buildScoringRulesView(userRules, accountRules),
        [userRules, accountRules],
    );

    const applicableForMeView = useMemo(
        () => buildScoringRulesView(userRules, applicableAccountRules),
        [applicableAccountRules, userRules],
    );

    const { privateCount, sharedCount } = useMemo(
        () => countScoringRulesByScope(userRules, accountRulesForSegments),
        [accountRulesForSegments, userRules],
    );

    const applicableForMeCount = useMemo(() => countRulesInScoringView(applicableForMeView), [applicableForMeView]);

    const segmentDisabled: Partial<Record<ScoringScopeSegment, boolean>> = {
        all: privateCount + sharedCount === 0,
        shared: sharedCount === 0,
        private: privateCount === 0,
        applicableForMe: applicableForMeCount === 0,
    };

    const [segmentIndex, setSegmentIndex] = useState(0);
    const requestedSegment = segments[segmentIndex] ?? segments[0];
    const segment = lockedSegment ?? (segmentDisabled[requestedSegment] ? "all" : requestedSegment);
    const effectiveSegmentIndex = segments.indexOf(segment);

    const activeCombinedView = usesFullAccountScope ? allAccountRulesView : combinedView;

    const filteredRules = useMemo(
        () =>
            getScoringRulesForSegment(
                segment,
                userRules,
                accountRulesForSegments,
                activeCombinedView,
                applicableForMeView,
            ),
        [accountRulesForSegments, activeCombinedView, applicableForMeView, segment, userRules],
    );
    const effectiveSelectedRuleId =
        isSelectionMode && !isMultiSelect ? resolveEffectiveSelectedRuleId(filteredRules, selectedRuleId) : undefined;

    const {
        wizardState,
        setWizardState,
        handleWizardClose,
        deleteState,
        setDeleteState,
        deleteError,
        clearDeleteError,
        resetDeleteState,
        confirmDeleteIfReady,
        handleDeleteModalClose,
        getDeletePersistOptions,
    } = useScoringBoardModalState<WizardState, DeleteRuleState>(scoringBoardTracking);

    const isInitialLoading =
        (isUserFetching && userConfig === undefined) || (isAccountFetching && accountConfig === undefined);
    const needsTeamFilteredRules = !usesFullAccountScope || showApplicableForMeSegment;
    const isTeamsMembershipLoading = isMembershipLoading && needsTeamFilteredRules;
    const isFetching = isUserFetching || isAccountFetching;
    const isSaving = isSavingUser || isSavingAccount;
    const isError = isUserError || isAccountError;

    const getRulesForScope = useCallback(
        (scope: ScoringConfigType): ScoringConfigRule => (scope === ScoringConfigType.User ? userRules : accountRules),
        [userRules, accountRules],
    );

    const getSaveRulesForScope = useCallback(
        (scope: ScoringConfigType) => (scope === ScoringConfigType.User ? saveUserRules : saveAccountRules),
        [saveUserRules, saveAccountRules],
    );

    useEffect(() => {
        if (!wizardState) {
            return;
        }

        dispatch(setSignalsSourceAction(getScoringConfigSignalSource(wizardState.scope)));

        return () => {
            dispatch(setSignalsSourceAction(getScoringConfigSignalSource(ScoringConfigType.User)));
        };
    }, [dispatch, wizardState]);

    const persist = (
        scope: ScoringConfigType,
        nextRules: ScoringConfigRule,
        successKey: string,
        onDone: () => void,
        options?: { errorPrefixKey?: string; onErrorMessage?: (message: string) => void },
    ): void => {
        persistRules(getSaveRulesForScope(scope), nextRules, successKey, onDone, options);
    };

    const handleAddRule = useCallback(
        (priority: LeadPriorityScore, scope: ScoringConfigType) => {
            scoringBoardTracking.trackAddRule(priority);
            setWizardState({ mode: "create", priority, scope });
        },
        [scoringBoardTracking],
    );

    const resolveRuleScope = useCallback(
        (rule: IScoringRuleDto): ScoringConfigType | undefined => {
            if (!rule.id) {
                return undefined;
            }

            if (segment === "private") {
                return ScoringConfigType.User;
            }

            if (segment === "shared") {
                return ScoringConfigType.Account;
            }

            if (segment === "applicableForMe") {
                return applicableForMeView.ruleScopeById[rule.id];
            }

            return activeCombinedView.ruleScopeById[rule.id];
        },
        [activeCombinedView.ruleScopeById, applicableForMeView.ruleScopeById, segment],
    );

    const handleEditRule = (priority: LeadPriorityScore, ruleIndex: number) => {
        const location = resolveScopedRuleLocation(
            filteredRules,
            priority,
            ruleIndex,
            resolveRuleScope,
            getRulesForScope,
        );

        if (!location) {
            return;
        }

        scoringBoardTracking.trackOpenEditRule();
        setWizardState({
            mode: "edit",
            priority,
            ruleIndex: location.sourceIndex,
            rule: location.rule,
            scope: location.scope,
        });
    };

    const handleDeleteRequest = (priority: LeadPriorityScore, ruleIndex: number) => {
        const location = resolveScopedRuleLocation(
            filteredRules,
            priority,
            ruleIndex,
            resolveRuleScope,
            getRulesForScope,
        );

        if (!location) {
            return;
        }

        scoringBoardTracking.trackRequestDeleteRule();
        clearDeleteError();
        setDeleteState({ priority, ruleIndex: location.sourceIndex, scope: location.scope });
    };

    const handleDeleteConfirm = () => {
        const activeDeleteState = confirmDeleteIfReady();

        if (!activeDeleteState) {
            return;
        }

        clearDeleteError();

        const { priority, ruleIndex, scope } = activeDeleteState;
        const rules = getRulesForScope(scope);
        const { nextRules, deletedRuleId } = removeRuleFromColumn(rules, priority, ruleIndex);

        persist(
            scope,
            nextRules,
            "si.pages.score_settings.toast.rule_deleted",
            () => {
                if (isMultiSelect) {
                    if (deletedRuleId) {
                        onRuleDeleted?.(deletedRuleId);
                    }
                } else if (isSelectionMode && onSelect) {
                    const nextUserRules = scope === ScoringConfigType.User ? nextRules : userRules;
                    const nextFullAccountRules = scope === ScoringConfigType.Account ? nextRules : accountRules;
                    const nextApplicableAccountRules = isMembershipSuccess
                        ? filterAccountRulesByTeamMembership(nextFullAccountRules, userTeamIds)
                        : nextFullAccountRules;
                    const nextVisibleAccountRules = usesFullAccountScope
                        ? nextFullAccountRules
                        : nextApplicableAccountRules;
                    const nextActiveCombinedView = usesFullAccountScope
                        ? buildScoringRulesView(nextUserRules, nextFullAccountRules)
                        : buildScoringRulesView(nextUserRules, nextVisibleAccountRules);
                    const nextApplicableForMeView = buildScoringRulesView(nextUserRules, nextApplicableAccountRules);
                    const nextFiltered = getScoringRulesForSegment(
                        segment,
                        nextUserRules,
                        nextVisibleAccountRules,
                        nextActiveCombinedView,
                        nextApplicableForMeView,
                    );
                    reconcileSelectedRuleAfterDelete(deletedRuleId, effectiveSelectedRuleId, nextFiltered, onSelect);
                }

                resetDeleteState();
            },
            getDeletePersistOptions(),
        );
    };

    const handleWizardSave = (rule: IScoringRuleDto) => {
        if (!wizardState) {
            return;
        }

        const { priority, scope } = wizardState;
        const rules = getRulesForScope(scope);
        const { nextRules, successKey, isCreate } = prepareWizardRuleUpdate(rules, wizardState, rule);

        persist(scope, nextRules, successKey, () => {
            scoringBoardTracking.trackSaveRuleWizard(isCreate ? "create" : "edit");
            handleWizardClose();

            if (isSelectionMode && onSelect && isCreate && rule.id) {
                onSelect(rule.id, priority);
            }
        });
    };

    const handleMoveRule = (
        sourcePriority: LeadPriorityScore,
        ruleIndex: number,
        targetPriority: LeadPriorityScore,
    ) => {
        const location = resolveScopedRuleLocation(
            filteredRules,
            sourcePriority,
            ruleIndex,
            resolveRuleScope,
            getRulesForScope,
        );

        if (!location) {
            return;
        }

        const scopeRules = getRulesForScope(location.scope);
        const moveResult = moveRuleBetweenColumns(scopeRules, sourcePriority, location.sourceIndex, targetPriority);

        if (!moveResult) {
            return;
        }

        scoringBoardTracking.trackMoveRule(targetPriority);
        persistOptimistic(
            getSaveRulesForScope(location.scope),
            moveResult.nextRules,
            "si.pages.score_settings.toast.rule_moved",
            () => undefined,
        );
    };

    const handleSelectRule = (ruleId: string, priority: LeadPriorityScore) => {
        scoringBoardTracking.trackSelectRule(segment);
        onSelect?.(ruleId, priority);
    };

    const canEditRule = useCallback(
        (rule: IScoringRuleDto) => {
            const scope = resolveRuleScope(rule);

            return scope === ScoringConfigType.User || canManageShared;
        },
        [canManageShared, resolveRuleScope],
    );

    const getRuleScope = useCallback((rule: IScoringRuleDto) => resolveRuleScope(rule), [resolveRuleScope]);

    const renderAddRuleControl = useCallback(
        (priority: LeadPriorityScore) =>
            lockedScope !== undefined ? (
                <ScoringAddRuleButton
                    priority={priority}
                    scope={lockedScope}
                    automationPrefix={automationPrefix}
                    onAddRule={(scope) => handleAddRule(priority, scope)}
                />
            ) : (
                <ScoringAddRuleCombobox
                    priority={priority}
                    canCreateShared={canManageShared}
                    automationPrefix={automationPrefix}
                    addRuleTranslationKeys={addRuleTranslationKeys}
                    onAddRule={(scope) => handleAddRule(priority, scope)}
                />
            ),
        [addRuleTranslationKeys, automationPrefix, handleAddRule, canManageShared, lockedScope],
    );

    if (isInitialLoading || isBootstrapping || isTeamsMembershipLoading) {
        return <ScoringBoardLoader />;
    }

    if (isError) {
        return (
            <ScoringBoardError
                onRetry={() => {
                    scoringBoardTracking.trackBoardRetry();
                    void refetchUser();
                    void refetchAccount();
                }}
                dataAutomation={`${automationPrefix}-retry`}
            />
        );
    }

    return (
        <>
            <Stack gap="16px">
                {segmentTranslationKeys ? (
                    <ScoringBoardHeader
                        segments={segments}
                        segmentTranslationKeys={segmentTranslationKeys}
                        segmentDisabled={segmentDisabled}
                        selectedIndex={effectiveSegmentIndex}
                        onSegmentChange={setSegmentIndex}
                        actions={
                            isExportEnabled ? (
                                <ScoringRulesExportButton
                                    rules={filteredRules}
                                    getRuleScope={getRuleScope}
                                    filtersList={filtersList}
                                    signalById={signalById}
                                    segment={segment}
                                    automationPrefix={automationPrefix}
                                />
                            ) : null
                        }
                    />
                ) : null}

                <ScoringBoard
                    columns={SCORING_PRIORITY_COLUMNS}
                    rules={filteredRules}
                    filtersList={filtersList}
                    signalById={signalById}
                    onAddRule={() => undefined}
                    onEditRule={handleEditRule}
                    onDeleteRule={handleDeleteRequest}
                    onMoveRule={isSelectionMode ? undefined : handleMoveRule}
                    addRuleControl={renderAddRuleControl}
                    getRuleScope={getRuleScope}
                    canEditRule={canEditRule}
                    getRuleUsage={getRuleUsage}
                    selectedRuleId={effectiveSelectedRuleId}
                    selectedRuleIds={selectedRuleIds}
                    pinSelectedFirst={pinSelectedFirst}
                    showUsedBadgeForSelected={showUsedBadgeForSelected}
                    onSelect={isSelectionMode ? handleSelectRule : undefined}
                    getSelectionDisabledReason={getSelectionDisabledReason}
                    automationPrefix={automationPrefix}
                />
            </Stack>

            {wizardState ? (
                <ScoringRuleWizardModal
                    isOpen
                    mode={wizardState.mode}
                    priority={wizardState.priority}
                    configType={wizardState.scope}
                    initialRule={wizardState.mode === "edit" ? wizardState.rule : undefined}
                    defaultTeamIds={defaultTeamIds}
                    onClose={handleWizardClose}
                    onSave={handleWizardSave}
                    isSaving={isSaving || isFetching}
                />
            ) : null}

            <ScoringRuleDeleteModal
                errorMessage={deleteError}
                isOpen={deleteState !== null}
                isSubmitLoading={isSaving}
                onClose={handleDeleteModalClose}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};
